/**
 * 예산차수등록 (tenant · 회계연도별 · BdBudgetRound API / localStorage PoC)
 * - 상단: 예산차수 + 결합코드(budgetKindCode)
 * - 하단: 요구/조정 차수
 * - 팝업: 조정차수별 부서 편성집계·제출/접수/조정
 *
 * 예산구분 3종: 본예산(a) / 추경예산(b) / 간주예산(d)
 * 결합코드 (연도 내 유일):
 * - 본예산 = 100
 * - 추경 n차 본편 = 210, 220…  (200 + n×10 + 0)
 * - 추경 n차 성립전 k = 211, 212… (200 + n×10 + k)  ← 끝자리 = 성립전 차수
 * - 간주 = 301, 302… (300 + classSeq, 최대 100회)
 *
 * 계상 그룹: 211·212 → 추경 본편 210 (settleParentCode)
 *
 * API 모드(isBdApiEnabled)에서는 차수·조정단계·부서제출을 BdBudgetRound 로 저장하고,
 * 동기 조회 헬퍼(결합코드 옵션·컨텍스트 등)는 load 가 채운 메모리 캐시를 읽는다.
 */

import { loadDepts, filterDepts } from 'master/deptStore';
import { isBdApiEnabled } from 'config/bdApiConfig';
import { commonSelect, commonSaveList, cacheGet, cacheSet } from 'services/commonQuery';

export const BUDGET_ROUND_STORAGE_PREFIX = 'budgetRound';

const ROUND_SAVE_QUERY = [
  'BdBudgetRound.insertRound',
  'BdBudgetRound.updateRound',
  'BdBudgetRound.deleteRound',
];
const ADJ_SAVE_QUERY = [
  'BdBudgetRound.insertRoundAdj',
  'BdBudgetRound.updateRoundAdj',
  'BdBudgetRound.deleteRoundAdj',
];
const DEPT_SAVE_QUERY = [
  'BdBudgetRound.insertRoundDept',
  'BdBudgetRound.updateRoundDept',
  'BdBudgetRound.deleteRoundDept',
];

const roundCache = new Map();

export const BUDGET_CLASS = {
  MAIN: 'a',
  SUPP: 'b',
  DEEMED: 'd',
};

export const BUDGET_CLASS_OPTIONS = [
  { value: 'a', label: '본예산' },
  { value: 'b', label: '추경예산' },
  { value: 'd', label: '간주예산' },
];

const LEGACY_CLASS_MAP = {
  본예산: 'a',
  추경예산: 'b',
  성립전예산: 'b', // 구 구분 → 추경 + preSeq
  간주예산: 'd',
  a: 'a',
  b: 'b',
  c: 'b',
  d: 'd',
};

export const BUDGET_CLASS_LABEL = {
  a: '본예산',
  b: '추경예산',
  d: '간주예산',
};

export const YN_OPTIONS = [
  { value: 'Y', label: 'Y' },
  { value: 'N', label: 'N' },
];

export const INOUT_OPTIONS = [
  { value: '지출', label: '지출' },
  { value: '수입', label: '수입' },
];

/** PoC 샘플 예산부서 */
export const SAMPLE_BUDGET_DEPTS = [
  { deptCode: '220', deptName: '총무' },
  { deptCode: '230', deptName: '기획' },
  { deptCode: '240', deptName: '재무' },
  { deptCode: '310', deptName: '사업1' },
  { deptCode: '320', deptName: '사업2' },
];

export const MAIN_KIND_CODE = '100';
export const MAX_DEEMED_SEQ = 100;
export const MAX_PRE_SEQ = 9;

/** 예산차수등록 저장 후 keep-mounted 편성요구 등 결합코드 옵션 갱신 */
export const BUDGET_ROUNDS_CHANGED_EVENT = 'erp:budget-rounds-changed';

export function notifyBudgetRoundsChanged(tenantCode, fiscalYear) {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
  window.dispatchEvent(
    new CustomEvent(BUDGET_ROUNDS_CHANGED_EVENT, {
      detail: {
        tenantCode: String(tenantCode || ''),
        fiscalYear: String(fiscalYear || ''),
      },
    })
  );
}

export function createRoundUid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `BR-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function budgetRoundStorageKey(tenantCode, fiscalYear) {
  return [BUDGET_ROUND_STORAGE_PREFIX, tenantCode || '', fiscalYear || ''].join('|');
}

export function normalizeBudgetClass(value) {
  if (value == null || value === '') return BUDGET_CLASS.MAIN;
  const mapped = LEGACY_CLASS_MAP[value];
  if (mapped) return mapped;
  return LEGACY_CLASS_MAP[String(value).trim()] || BUDGET_CLASS.MAIN;
}

/** 추경 n차·성립전 k → 210 / 211 / 212 / 220… */
export function encodeSuppKindCode(classSeq, preSeq = 0) {
  const seq = Number(classSeq);
  const pre = Number(preSeq) || 0;
  if (!Number.isFinite(seq) || seq < 1) return '';
  if (!Number.isFinite(pre) || pre < 0 || pre > MAX_PRE_SEQ) return '';
  return String(200 + seq * 10 + pre);
}

/** 계상 대상 추경 본편 코드 (211 → 210) */
export function settleParentCode(classSeq) {
  return encodeSuppKindCode(classSeq, 0);
}

/** 간주 n차: 301…400 */
export function encodeDeemedKindCode(classSeq) {
  const seq = Number(classSeq);
  if (!Number.isFinite(seq) || seq < 1 || seq > MAX_DEEMED_SEQ) return '';
  return String(300 + seq);
}

/**
 * @param {{ budgetClass: string, classSeq?: number, preSeq?: number }} p
 */
export function encodeBudgetKindCode({ budgetClass, classSeq, preSeq } = {}) {
  const cls = normalizeBudgetClass(budgetClass);
  if (cls === BUDGET_CLASS.MAIN) return MAIN_KIND_CODE;
  if (cls === BUDGET_CLASS.SUPP) return encodeSuppKindCode(classSeq ?? 1, preSeq ?? 0);
  if (cls === BUDGET_CLASS.DEEMED) return encodeDeemedKindCode(classSeq ?? 1);
  return '';
}

/** 210~299 코드 → { classSeq, preSeq } */
export function parseSuppKindCode(code) {
  const raw = String(code || '').replace(/b$/i, '');
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 210 || n > 299) return null;
  const preSeq = n % 10;
  const classSeq = Math.floor((n - 200) / 10);
  if (classSeq < 1) return null;
  return { classSeq, preSeq };
}

/** 그리드 삭제 표시(D) 행은 시퀀스·검증 대상에서 제외 */
export function isActiveModRow(row) {
  return row && row.mod !== 'D';
}

export function nextClassSeq(rounds = [], budgetClass) {
  const cls = normalizeBudgetClass(budgetClass);
  if (cls === BUDGET_CLASS.MAIN) return 1;
  let max = 0;
  for (const r of rounds || []) {
    if (!isActiveModRow(r)) continue;
    if (normalizeBudgetClass(r.budgetClass) !== cls) continue;
    // 추경: 본편·성립전 모두 동일 classSeq 그룹 — max classSeq 기준
    const n = Number(r.classSeq);
    if (Number.isFinite(n) && n > max) max = n;
  }
  const next = max + 1;
  if (cls === BUDGET_CLASS.DEEMED && next > MAX_DEEMED_SEQ) return null;
  return next;
}

/** 동일 추경 차수 내 다음 성립전 차수 (1~9) */
export function nextPreSeq(rounds = [], classSeq) {
  const seq = Number(classSeq);
  let max = 0;
  for (const r of rounds || []) {
    if (!isActiveModRow(r)) continue;
    if (normalizeBudgetClass(r.budgetClass) !== BUDGET_CLASS.SUPP) continue;
    if (Number(r.classSeq) !== seq) continue;
    const pre = Number(r.preSeq) || 0;
    if (pre > max) max = pre;
  }
  if (max >= MAX_PRE_SEQ) return null;
  return max + 1;
}

/**
 * 구 데이터 정규화
 * - 성립전(c) / 210b → 추경 + preSeq
 */
export function normalizeRoundRow(row, fiscalYear) {
  if (!row || typeof row !== 'object') return row;
  const y = String(fiscalYear || new Date().getFullYear());
  let budgetClass = normalizeBudgetClass(row.budgetClass);
  let classSeq = Number(row.classSeq);
  let preSeq = Number(row.preSeq);
  let budgetKindCode = row.budgetKindCode ? String(row.budgetKindCode) : '';

  // 구 성립전 구분 / 210b / settleToCode → 추경 + preSeq
  const legacyPre =
    row.budgetClass === 'c' ||
    row.budgetClass === '성립전예산' ||
    /b$/i.test(budgetKindCode) ||
    (row.settleToCode != null && row.settleToCode !== '');
  if (legacyPre) {
    budgetClass = BUDGET_CLASS.SUPP;
    const raw = String(row.settleToCode || budgetKindCode || '').replace(/b$/i, '');
    const p = parseSuppKindCode(raw) || parseSuppKindCode(encodeSuppKindCode(classSeq || 1, 0));
    if (p) {
      classSeq = p.classSeq;
      if (!Number.isFinite(preSeq) || preSeq <= 0) {
        preSeq = p.preSeq > 0 ? p.preSeq : 1;
      }
    }
  }

  if (!Number.isFinite(classSeq) || classSeq < 1) {
    classSeq = Number(row.roundNo) || 1;
  }
  if (!Number.isFinite(preSeq) || preSeq < 0) {
    preSeq = 0;
  }

  if (budgetClass === BUDGET_CLASS.MAIN) {
    classSeq = 1;
    preSeq = 0;
    budgetKindCode = MAIN_KIND_CODE;
  } else if (budgetClass === BUDGET_CLASS.SUPP) {
    if (budgetKindCode && (!row.classSeq || row.preSeq == null)) {
      const p = parseSuppKindCode(budgetKindCode.replace(/b$/i, ''));
      if (p) {
        if (!row.classSeq) classSeq = p.classSeq;
        if (row.preSeq == null && !/b$/i.test(String(row.budgetKindCode || ''))) {
          preSeq = p.preSeq;
        }
      }
    }
    if (preSeq > MAX_PRE_SEQ) preSeq = MAX_PRE_SEQ;
    budgetKindCode = encodeBudgetKindCode({ budgetClass, classSeq, preSeq });
  } else if (budgetClass === BUDGET_CLASS.DEEMED) {
    preSeq = 0;
    if (budgetKindCode && !row.classSeq) {
      const n = Number(budgetKindCode);
      if (Number.isFinite(n) && n >= 301 && n <= 400) classSeq = n - 300;
    }
    budgetKindCode = encodeBudgetKindCode({ budgetClass, classSeq, preSeq: 0 });
  }

  const label = BUDGET_CLASS_LABEL[budgetClass] || '';
  let roundName = row.roundName;
  if (!roundName) {
    if (budgetClass === BUDGET_CLASS.SUPP) {
      roundName =
        preSeq > 0 ? `추경${classSeq}차 성립전${preSeq}` : `추경${classSeq}차`;
    } else if (budgetClass === BUDGET_CLASS.DEEMED) {
      roundName = `간주${classSeq}차`;
    } else {
      roundName = label;
    }
  }

  const { settleToCode: _drop, ...rest } = row;
  return {
    ...rest,
    budgetClass,
    classSeq,
    preSeq,
    budgetKindCode,
    settleParentCode:
      budgetClass === BUDGET_CLASS.SUPP ? settleParentCode(classSeq) : null,
    roundName,
    applyDate: row.applyDate || `${y}0101`,
    planUseYn: row.planUseYn || 'N',
    budgetConfirmYn: row.budgetConfirmYn || 'N',
    adjStages: (row.adjStages || []).map(syncStageAmounts),
  };
}

/**
 * @returns {{ ok: boolean, errors: string[], warnings: string[] }}
 */
export function validateRoundCodes(rounds = []) {
  const errors = [];
  const warnings = [];
  const list = (rounds || []).filter(isActiveModRow).map((r) => normalizeRoundRow(r));
  const kindMap = new Map();

  const mains = list.filter((r) => r.budgetClass === BUDGET_CLASS.MAIN);
  if (mains.length > 1) {
    errors.push('본예산은 회계연도당 1건만 등록할 수 있습니다.');
  }
  mains.forEach((r) => {
    if (r.budgetKindCode !== MAIN_KIND_CODE) {
      errors.push(`본예산 결합코드는 ${MAIN_KIND_CODE}이어야 합니다.`);
    }
  });

  list.forEach((r, idx) => {
    const label = r.budgetKindCode || r.roundName || `#${idx + 1}`;
    if (!r.budgetKindCode) {
      errors.push(`${label}: 결합코드가 없습니다.`);
      return;
    }
    if (kindMap.has(r.budgetKindCode)) {
      errors.push(`결합코드 중복: ${r.budgetKindCode}`);
    } else {
      kindMap.set(r.budgetKindCode, r);
    }

    if (r.budgetClass === BUDGET_CLASS.SUPP) {
      const expected = encodeSuppKindCode(r.classSeq, r.preSeq);
      if (r.budgetKindCode !== expected) {
        errors.push(`${label}: 추경/성립전 코드는 ${expected}이어야 합니다. (차수${r.classSeq}, 성립전${r.preSeq})`);
      }
      if (Number(r.preSeq) > 0) {
        const parent = settleParentCode(r.classSeq);
        const hasParent = list.some(
          (x) =>
            x.budgetClass === BUDGET_CLASS.SUPP &&
            Number(x.classSeq) === Number(r.classSeq) &&
            Number(x.preSeq) === 0
        );
        if (!hasParent) {
          warnings.push(
            `${r.budgetKindCode}: 계상 추경 본편 ${parent}이(가) 아직 없습니다. (선행 등록 허용)`
          );
        }
      }
    }
    if (r.budgetClass === BUDGET_CLASS.DEEMED) {
      const seq = Number(r.classSeq);
      if (!Number.isFinite(seq) || seq < 1 || seq > MAX_DEEMED_SEQ) {
        errors.push(`${label}: 간주 차수는 1~${MAX_DEEMED_SEQ}입니다.`);
      }
      const expected = encodeDeemedKindCode(seq);
      if (expected && r.budgetKindCode !== expected) {
        errors.push(`${label}: 간주 코드는 ${expected}이어야 합니다.`);
      }
    }
  });

  return { ok: errors.length === 0, errors, warnings };
}

/** 추경 본편 삭제 시 같은 차수 성립전이 있으면 차단 */
export function getDeleteRoundBlockReason(rounds, roundUid) {
  const list = (rounds || []).filter(isActiveModRow).map(normalizeRoundRow);
  const target = list.find((r) => r.roundUid === roundUid);
  if (!target) return null;
  if (target.budgetClass === BUDGET_CLASS.SUPP && Number(target.preSeq) === 0) {
    const dependents = list.filter(
      (r) =>
        r.roundUid !== target.roundUid &&
        r.budgetClass === BUDGET_CLASS.SUPP &&
        Number(r.classSeq) === Number(target.classSeq) &&
        Number(r.preSeq) > 0
    );
    if (dependents.length) {
      return `같은 추경 차수의 성립전(${dependents.map((d) => d.budgetKindCode).join(', ')})이 있어 삭제할 수 없습니다.`;
    }
  }
  return null;
}

/**
 * @param {object} [opts]
 * @param {number} [opts.preSeq] - 성립전 차수 (0=본편)
 * @param {number} [opts.classSeq] - 추경/간주 차수
 */
export function createRoundForClass(fiscalYear, budgetClass, rounds = [], partial = {}) {
  const cls = normalizeBudgetClass(budgetClass || partial.budgetClass || BUDGET_CLASS.SUPP);
  const y = String(fiscalYear || new Date().getFullYear());
  let classSeq = 1;
  let preSeq = 0;

  if (cls === BUDGET_CLASS.MAIN) {
    classSeq = 1;
    preSeq = 0;
  } else if (cls === BUDGET_CLASS.SUPP) {
    if (partial.preSeq != null && Number(partial.preSeq) > 0) {
      classSeq = Number(partial.classSeq) || nextClassSeq(rounds, BUDGET_CLASS.SUPP) || 1;
      // 성립전: classSeq가 명시되지 않으면 선택 행/최대 추경 차수
      if (partial.classSeq == null) {
        let maxBase = 0;
        rounds.forEach((r) => {
          if (!isActiveModRow(r)) return;
          if (normalizeBudgetClass(r.budgetClass) !== BUDGET_CLASS.SUPP) return;
          maxBase = Math.max(maxBase, Number(r.classSeq) || 0);
        });
        classSeq = maxBase || 1;
      }
      const np = nextPreSeq(
        rounds.filter((r) => Number(r.classSeq) === Number(classSeq)),
        classSeq
      );
      preSeq = Number(partial.preSeq) || np || 1;
      if (preSeq > MAX_PRE_SEQ) {
        return { ok: false, reason: `성립전은 추경 차수당 최대 ${MAX_PRE_SEQ}회입니다.` };
      }
    } else {
      classSeq = Number(partial.classSeq) || nextClassSeq(rounds, BUDGET_CLASS.SUPP);
      preSeq = 0;
    }
  } else {
    classSeq = Number(partial.classSeq) || nextClassSeq(rounds, cls);
    preSeq = 0;
    if (classSeq == null) {
      return { ok: false, reason: `간주예산은 최대 ${MAX_DEEMED_SEQ}회까지입니다.` };
    }
  }

  const budgetKindCode = encodeBudgetKindCode({ budgetClass: cls, classSeq, preSeq });
  const roundName =
    cls === BUDGET_CLASS.MAIN
      ? '본예산'
      : cls === BUDGET_CLASS.SUPP
        ? preSeq > 0
          ? `추경${classSeq}차 성립전${preSeq}`
          : `추경${classSeq}차`
        : `간주${classSeq}차`;

  const row = normalizeRoundRow(
    {
      roundUid: createRoundUid(),
      roundNo: nextRoundNo(rounds),
      budgetClass: cls,
      classSeq,
      preSeq,
      budgetKindCode,
      roundName,
      applyDate: `${y}0101`,
      remark: '',
      planUseYn: 'N',
      budgetConfirmYn: 'N',
      budgetConfirmAt: null,
      budgetConfirmBy: null,
      finalAdjNo: null,
      adjStages: [], // 요구/조정차수는 디테일 그리드에서 별도 추가
      ...partial,
      budgetClass: cls,
      classSeq,
      preSeq,
      budgetKindCode: encodeBudgetKindCode({
        budgetClass: cls,
        classSeq,
        preSeq,
      }),
    },
    y
  );
  return { ok: true, row };
}

/** 전년 이관 등에서만 사용. 신규 예산차수는 adjStages=[] 후 디테일에서 추가 */
export function defaultAdjStages(fiscalYear) {
  const y = String(fiscalYear || new Date().getFullYear());
  return [
    {
      adjNo: 1,
      adjName: '부서요구',
      inputDeadline: `${y}-03-31`,
      amtBefore: 0,
      amtAfter: 0,
      remark: '',
      adjConfirmYn: 'N',
      adjConfirmAt: null,
      adjConfirmBy: null,
      deptRows: [],
    },
    {
      adjNo: 2,
      adjName: '1차조정',
      inputDeadline: `${y}-04-30`,
      amtBefore: 0,
      amtAfter: 0,
      remark: '',
      adjConfirmYn: 'N',
      adjConfirmAt: null,
      adjConfirmBy: null,
      deptRows: [],
    },
    {
      adjNo: 3,
      adjName: '2차조정',
      inputDeadline: `${y}-05-31`,
      amtBefore: 0,
      amtAfter: 0,
      remark: '',
      adjConfirmYn: 'N',
      adjConfirmAt: null,
      adjConfirmBy: null,
      deptRows: [],
    },
  ];
}

export function defaultRoundRow(fiscalYear, partial = {}) {
  const y = String(fiscalYear || new Date().getFullYear());
  return normalizeRoundRow(
    {
      roundUid: createRoundUid(),
      roundNo: 1,
      budgetClass: BUDGET_CLASS.MAIN,
      classSeq: 1,
      preSeq: 0,
      budgetKindCode: MAIN_KIND_CODE,
      roundName: '본예산',
      applyDate: `${y}0101`,
      remark: '',
      planUseYn: 'N',
      budgetConfirmYn: 'N',
      budgetConfirmAt: null,
      budgetConfirmBy: null,
      finalAdjNo: null,
      adjStages: [],
      ...partial,
    },
    y
  );
}

function roundCacheKey(tenantCode, fiscalYear) {
  return `${String(tenantCode || '').trim()}|${String(fiscalYear || '').trim()}`;
}

function readRoundsFromStorage(tenantCode, fiscalYear) {
  try {
    const raw = localStorage.getItem(budgetRoundStorageKey(tenantCode, fiscalYear));
    if (!raw) return [];
    const data = JSON.parse(raw);
    const rounds = Array.isArray(data?.rounds) ? data.rounds : [];
    return rounds.map((r) => normalizeRoundRow(r, fiscalYear));
  } catch {
    return [];
  }
}

/**
 * 동기 조회용 — load 가 채운 캐시(API) 또는 localStorage(로컬).
 * API 모드에서 아직 로드하지 않았으면 빈 배열.
 */
export function getCachedBudgetRounds(tenantCode, fiscalYear) {
  const key = roundCacheKey(tenantCode, fiscalYear);
  const cached = cacheGet(roundCache, key);
  if (cached) return cached;
  if (isBdApiEnabled()) return [];
  const rows = readRoundsFromStorage(tenantCode, fiscalYear);
  cacheSet(roundCache, key, rows);
  return rows;
}

/** DATETIME(3) 컬럼용 — FE ISO 문자열을 MySQL 이 받는 형태로 */
function toSqlTimestamp(value) {
  if (!value) return null;
  const s = String(value);
  if (!s.includes('T')) return s;
  return s.replace('T', ' ').replace(/Z$/, '');
}

function emptyToNull(value) {
  if (value == null) return null;
  const s = String(value).trim();
  return s === '' ? null : s;
}

/** 조정단계·부서행 PK 보강 (API 재적재 시 동일 uid 유지) */
function ensureRoundChildIds(round) {
  const adjStages = (round.adjStages || []).map((stage) => ({
    ...stage,
    adjUid: stage.adjUid || createRoundUid(),
    deptRows: (stage.deptRows || []).map((d) => ({
      ...d,
      deptStatusUid: d.deptStatusUid || createRoundUid(),
    })),
  }));
  return { ...round, adjStages };
}

export async function loadBudgetRounds(tenantCode, fiscalYear) {
  const key = roundCacheKey(tenantCode, fiscalYear);
  if (isBdApiEnabled()) {
    const rows = await commonSelect('BdBudgetRound.selectRoundList', {
      tenantCode,
      fiscalYear,
    });
    const assembled = [];
    for (const round of rows) {
      const adjRows = await commonSelect('BdBudgetRound.selectRoundAdjList', {
        roundUid: round.roundUid,
      });
      const adjStages = [];
      for (const adj of adjRows) {
        const deptRows = await commonSelect('BdBudgetRound.selectRoundDeptList', {
          adjUid: adj.adjUid,
        });
        adjStages.push({
          ...adj,
          adjNo: Number(adj.adjNo),
          // DB accept_* ↔ FE receive_*
          deptRows: deptRows.map((d) => ({
            ...d,
            receiveYn: d.acceptYn === 'Y' ? 'Y' : 'N',
            receiveAt: d.acceptAt || null,
            receiveBy: d.acceptBy || null,
            mod: '',
          })),
          mod: '',
        });
      }
      assembled.push({ ...round, adjStages, mod: '' });
    }
    const normalized = assembled.map((r) => normalizeRoundRow(r, fiscalYear));
    cacheSet(roundCache, key, normalized);
    return normalized;
  }
  const rows = readRoundsFromStorage(tenantCode, fiscalYear);
  cacheSet(roundCache, key, rows);
  return rows;
}

/** 저장 직전 그리드 상태/임시키 제거 */
export function stripGridMeta(row) {
  if (!row || typeof row !== 'object') return row;
  const { mod, _tempId, budgetConfirmAtDisp, adjConfirmAtDisp, ...rest } = row;
  return rest;
}

export function commitRoundsForSave(rounds = []) {
  return (rounds || [])
    .filter(isActiveModRow)
    .map((r) => {
      const base = stripGridMeta(r);
      return {
        ...base,
        adjStages: (base.adjStages || [])
          .filter(isActiveModRow)
          .map((s) => stripGridMeta(s)),
      };
    });
}

/** 차수 1건 + 조정단계·부서제출 전체 교체 (API) */
async function persistRoundToApi(tenantCode, fiscalYear, round, exists) {
  await commonSaveList(
    ROUND_SAVE_QUERY,
    [
      {
        ...round,
        adjStages: undefined,
        applyDate: emptyToNull(toYyyymmdd(round.applyDate)),
        budgetConfirmAt: toSqlTimestamp(round.budgetConfirmAt),
        finalAdjNo: round.finalAdjNo == null || round.finalAdjNo === '' ? null : Number(round.finalAdjNo),
        mod: exists ? 'U' : 'A',
      },
    ],
    { tenantCode, fiscalYear }
  );

  // 조정단계·부서제출은 차수 단위로 전량 재적재 (부분 diff 대신 delete + insert)
  const oldAdjs = await commonSelect('BdBudgetRound.selectRoundAdjList', {
    roundUid: round.roundUid,
  });
  for (const adj of oldAdjs) {
    const oldDepts = await commonSelect('BdBudgetRound.selectRoundDeptList', {
      adjUid: adj.adjUid,
    });
    if (oldDepts.length) {
      await commonSaveList(
        DEPT_SAVE_QUERY,
        oldDepts.map((d) => ({ deptStatusUid: d.deptStatusUid, mod: 'D' }))
      );
    }
  }
  if (oldAdjs.length) {
    await commonSaveList(
      ADJ_SAVE_QUERY,
      oldAdjs.map((a) => ({ adjUid: a.adjUid, mod: 'D' }))
    );
  }

  for (const stage of round.adjStages || []) {
    await commonSaveList(
      ADJ_SAVE_QUERY,
      [
        {
          ...stage,
          deptRows: undefined,
          roundUid: round.roundUid,
          adjNo: Number(stage.adjNo) || 0,
          inputDeadline: emptyToNull(stage.inputDeadline),
          adjConfirmAt: toSqlTimestamp(stage.adjConfirmAt),
          mod: 'A',
        },
      ]
    );
    const deptRows = (stage.deptRows || [])
      .filter((d) => d && d.mod !== 'D' && String(d.deptCode || '').trim())
      .map((d) => ({
        ...d,
        adjUid: stage.adjUid,
        submitAt: toSqlTimestamp(d.submitAt),
        // FE receive_* → DB accept_*
        acceptYn: d.receiveYn === 'Y' ? 'Y' : 'N',
        acceptAt: toSqlTimestamp(d.receiveAt),
        acceptBy: d.receiveBy || null,
        mod: 'A',
      }));
    if (deptRows.length) {
      await commonSaveList(DEPT_SAVE_QUERY, deptRows);
    }
  }
}

export async function saveBudgetRounds(tenantCode, fiscalYear, rounds) {
  const key = budgetRoundStorageKey(tenantCode, fiscalYear);
  const normalized = commitRoundsForSave(rounds)
    .map((r) => normalizeRoundRow(r, fiscalYear))
    .map((r) => ensureRoundChildIds(r));
  // 동기 조회 헬퍼가 즉시 최신 상태를 보도록 캐시를 먼저 갱신
  cacheSet(roundCache, roundCacheKey(tenantCode, fiscalYear), normalized);

  if (isBdApiEnabled()) {
    const existing = await commonSelect('BdBudgetRound.selectRoundList', {
      tenantCode,
      fiscalYear,
    });
    const existingUids = new Set(existing.map((r) => String(r.roundUid)));

    const removed = (rounds || [])
      .filter((r) => r && r.mod === 'D' && existingUids.has(String(r.roundUid)))
      .map((r) => ({ roundUid: r.roundUid, mod: 'D' }));
    if (removed.length) {
      await commonSaveList(ROUND_SAVE_QUERY, removed, { tenantCode, fiscalYear });
    }

    for (const round of normalized) {
      if (!round.roundUid) continue;
      await persistRoundToApi(
        tenantCode,
        fiscalYear,
        round,
        existingUids.has(String(round.roundUid))
      );
    }
    const loaded = await loadBudgetRounds(tenantCode, fiscalYear);
    notifyBudgetRoundsChanged(tenantCode, fiscalYear);
    return loaded;
  }

  localStorage.setItem(
    key,
    JSON.stringify({
      tenantCode,
      fiscalYear,
      rounds: normalized,
      savedAt: new Date().toISOString(),
    })
  );
  notifyBudgetRoundsChanged(tenantCode, fiscalYear);
  return normalized;
}

/** PoC localStorage 예산차수 키 — 특정 기관·연도만 삭제 */
export function clearBudgetRoundStorageForTenant(tenantCode, fiscalYear) {
  if (typeof localStorage === 'undefined') return 0;
  const tc = String(tenantCode || '').trim();
  if (!tc) return 0;
  const prefix = fiscalYear
    ? `${BUDGET_ROUND_STORAGE_PREFIX}|${tc}|${fiscalYear}`
    : `${BUDGET_ROUND_STORAGE_PREFIX}|${tc}|`;
  const keys = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const k = localStorage.key(i);
    if (k && k.startsWith(prefix)) keys.push(k);
  }
  keys.forEach((k) => localStorage.removeItem(k));
  roundCache.delete(roundCacheKey(tc, fiscalYear));
  return keys.length;
}

/** @deprecated 전 기관 삭제 — 신규 코드에서 사용 금지 */
export function clearAllBudgetRoundStorage() {
  if (typeof localStorage === 'undefined') return 0;
  const keys = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const k = localStorage.key(i);
    if (k && k.startsWith(`${BUDGET_ROUND_STORAGE_PREFIX}|`)) keys.push(k);
  }
  keys.forEach((k) => localStorage.removeItem(k));
  roundCache.clear();
  return keys.length;
}

/** 연도별 예산차수 로드 (없으면 빈 배열 — 화면에서 신규 등록) */
export async function ensureBudgetRounds(tenantCode, fiscalYear) {
  const rounds = await loadBudgetRounds(tenantCode, fiscalYear);
  return rounds.map((r) => normalizeRoundRow(r, fiscalYear));
}

export function nextRoundNo(rounds = []) {
  let max = 0;
  for (const r of rounds) {
    if (!isActiveModRow(r)) continue;
    const n = Number(r?.roundNo);
    if (Number.isFinite(n) && n > max) max = n;
  }
  return max + 1;
}

export function nextAdjNo(stages = []) {
  let max = 0;
  for (const r of stages) {
    if (!isActiveModRow(r)) continue;
    const n = Number(r?.adjNo);
    if (Number.isFinite(n) && n > max) max = n;
  }
  return max + 1;
}

export function sumDeptAmounts(deptRows = []) {
  let before = 0;
  let after = 0;
  (deptRows || []).forEach((d) => {
    before += Number(d.amtBefore) || 0;
    after += Number(d.amtAfter) || 0;
  });
  return { amtBefore: before, amtAfter: after };
}

export function syncStageAmounts(stage) {
  const { amtBefore, amtAfter } = sumDeptAmounts(stage?.deptRows);
  return { ...stage, amtBefore, amtAfter };
}

/** 부서생성: 샘플 부서 중 없는 것만 추가 */
export function createMissingDeptRows(stage, inoutDiv = '지출') {
  const existing = new Set((stage?.deptRows || []).map((d) => `${d.inoutDiv}|${d.deptCode}`));
  const added = SAMPLE_BUDGET_DEPTS.filter((d) => !existing.has(`${inoutDiv}|${d.deptCode}`)).map(
    (d) => ({
      inoutDiv,
      deptCode: d.deptCode,
      deptName: d.deptName,
      amtBefore: 0,
      amtAfter: 0,
      submitYn: 'N',
      submitAt: null,
      submitBy: null,
      receiveYn: 'N',
      receiveAt: null,
      receiveBy: null,
      adjustYn: 'N',
      adjustAt: null,
      adjustBy: null,
    })
  );
  const deptRows = [...(stage?.deptRows || []), ...added];
  return syncStageAmounts({ ...stage, deptRows });
}

export function setDeptFlag(stage, targets, flag, userId) {
  const at = new Date().toISOString();
  const list = targets || [];
  const keySet = new Set();
  const codeOnly = new Set();
  for (const t of list) {
    if (t == null || t === '') continue;
    if (typeof t === 'string') {
      codeOnly.add(String(t).trim());
    } else if (t.deptCode) {
      keySet.add(`${t.inoutDiv || '지출'}|${String(t.deptCode).trim()}`);
    }
  }
  const match = (d) => {
    const code = String(d.deptCode || '').trim();
    const key = `${d.inoutDiv || '지출'}|${code}`;
    if (keySet.has(key)) return true;
    // 문자열 부서코드만 넘긴 경우(하위 호환): 동일 코드 전체 수지 대상
    if (!keySet.size && codeOnly.has(code)) return true;
    return false;
  };
  const deptRows = (stage?.deptRows || []).map((d) => {
    if (!match(d)) return d;
    if (flag === 'submit') {
      return { ...d, submitYn: 'Y', submitAt: at, submitBy: userId || '' };
    }
    if (flag === 'submitCancel') {
      return {
        ...d,
        submitYn: 'N',
        submitAt: null,
        submitBy: null,
        receiveYn: 'N',
        receiveAt: null,
        receiveBy: null,
        adjustYn: 'N',
        adjustAt: null,
        adjustBy: null,
      };
    }
    if (flag === 'receive') {
      if (d.submitYn !== 'Y') return d;
      return { ...d, receiveYn: 'Y', receiveAt: at, receiveBy: userId || '' };
    }
    // 편성조정: 제출 생략 — 접수 시 제출·접수 동시 Y
    if (flag === 'receiveAndSubmit') {
      return {
        ...d,
        submitYn: 'Y',
        submitAt: at,
        submitBy: userId || '',
        receiveYn: 'Y',
        receiveAt: at,
        receiveBy: userId || '',
      };
    }
    if (flag === 'receiveCancel') {
      return {
        ...d,
        receiveYn: 'N',
        receiveAt: null,
        receiveBy: null,
        adjustYn: 'N',
        adjustAt: null,
        adjustBy: null,
      };
    }
    // 편성조정 접수취소 — 제출·접수·조정 모두 해제
    if (flag === 'receiveCancelAndSubmit') {
      return {
        ...d,
        submitYn: 'N',
        submitAt: null,
        submitBy: null,
        receiveYn: 'N',
        receiveAt: null,
        receiveBy: null,
        adjustYn: 'N',
        adjustAt: null,
        adjustBy: null,
      };
    }
    if (flag === 'adjust') {
      if (d.receiveYn !== 'Y') return d;
      return { ...d, adjustYn: 'Y', adjustAt: at, adjustBy: userId || '' };
    }
    if (flag === 'adjustCancel') {
      return { ...d, adjustYn: 'N', adjustAt: null, adjustBy: null };
    }
    return d;
  });
  return syncStageAmounts({ ...stage, deptRows });
}

/**
 * 전년 본예산(또는 마지막 차수) → 당해 작성중 차수 생성
 */
export async function createBudgetFromPrevYear(tenantCode, fiscalYear, { userId } = {}) {
  const y = String(fiscalYear);
  const prevY = String(Number(y) - 1);
  if (!Number.isFinite(Number(y))) {
    return { ok: false, reason: '회계년도가 올바르지 않습니다.' };
  }
  const current = await loadBudgetRounds(tenantCode, y);
  const hasMain = current.some((r) => normalizeBudgetClass(r.budgetClass) === BUDGET_CLASS.MAIN);
  if (hasMain) {
    return { ok: false, reason: `${y}년 본예산 차수가 이미 있습니다.` };
  }

  const prevRounds = await loadBudgetRounds(tenantCode, prevY);
  const src =
    prevRounds.find(
      (r) => normalizeBudgetClass(r.budgetClass) === BUDGET_CLASS.MAIN && r.budgetConfirmYn === 'Y'
    ) ||
    prevRounds.find((r) => normalizeBudgetClass(r.budgetClass) === BUDGET_CLASS.MAIN) ||
    prevRounds[prevRounds.length - 1];

  if (!src) {
    const seeded = defaultRoundRow(y, {
      roundNo: nextRoundNo(current),
      remark: `${prevY}년 데이터 없음 — 신규 본예산`,
    });
    const next = [...current, seeded];
    await saveBudgetRounds(tenantCode, y, next);
    return { ok: true, rounds: next, created: seeded, fromSeed: true };
  }

  const clonedStages = (src.adjStages || defaultAdjStages(y)).map((s, idx) =>
    syncStageAmounts({
      ...s,
      adjNo: idx + 1,
      inputDeadline: s.inputDeadline
        ? String(s.inputDeadline).replace(String(prevY), y)
        : `${y}-03-31`,
      adjConfirmYn: 'N',
      adjConfirmAt: null,
      adjConfirmBy: null,
      deptRows: (s.deptRows || []).map((d) => ({
        ...d,
        submitYn: 'N',
        submitAt: null,
        submitBy: null,
        receiveYn: 'N',
        receiveAt: null,
        receiveBy: null,
        adjustYn: 'N',
        adjustAt: null,
        adjustBy: null,
      })),
    })
  );

  const created = defaultRoundRow(y, {
    roundNo: nextRoundNo(current),
    budgetClass: BUDGET_CLASS.MAIN,
    classSeq: 1,
    preSeq: 0,
    budgetKindCode: MAIN_KIND_CODE,
    roundName: src.roundName || '본예산',
    applyDate: `${y}0101`,
    planUseYn: src.planUseYn || 'N',
    remark: `${prevY}년 차수 이관 (${userId || ''})`,
    budgetConfirmYn: 'N',
    budgetConfirmAt: null,
    budgetConfirmBy: null,
    finalAdjNo: null,
    adjStages: clonedStages,
  });

  const next = [...current, created];
  await saveBudgetRounds(tenantCode, y, next);
  return { ok: true, rounds: next, created, fromSeed: false };
}

/** 이전 조정차수 금액을 다음 작성 차수로 복사(부서행 포함) */
export function copyPrevAdjToStage(round, targetAdjNo) {
  const stages = [...(round?.adjStages || [])].sort((a, b) => Number(a.adjNo) - Number(b.adjNo));
  const idx = stages.findIndex((s) => Number(s.adjNo) === Number(targetAdjNo));
  if (idx <= 0) return { ok: false, reason: '복사할 이전 조정차수가 없습니다.' };
  const prev = stages[idx - 1];
  const cur = stages[idx];
  if (cur.adjConfirmYn === 'Y') {
    return { ok: false, reason: '확정된 조정차수에는 복사할 수 없습니다.' };
  }
  const deptRows = (prev.deptRows || []).map((d) => ({
    ...d,
    amtBefore: Number(d.amtAfter) || Number(d.amtBefore) || 0,
    amtAfter: Number(d.amtAfter) || Number(d.amtBefore) || 0,
    submitYn: 'N',
    submitAt: null,
    submitBy: null,
    receiveYn: 'N',
    receiveAt: null,
    receiveBy: null,
    adjustYn: 'N',
    adjustAt: null,
    adjustBy: null,
  }));
  stages[idx] = syncStageAmounts({ ...cur, deptRows });
  return { ok: true, round: { ...round, adjStages: stages } };
}

export function computeFinalAdjNo(stages = []) {
  const confirmed = (stages || [])
    .filter((s) => s.adjConfirmYn === 'Y')
    .sort((a, b) => Number(b.adjNo) - Number(a.adjNo));
  return confirmed[0] ? Number(confirmed[0].adjNo) : null;
}

/**
 * 그리드 편집 후 구분·차수·성립전에 맞춰 결합코드 재계산
 */
export function syncRoundKindFields(row, allRounds = []) {
  const others = (allRounds || []).filter((r) => r.roundUid !== row.roundUid);
  let budgetClass = normalizeBudgetClass(row.budgetClass);
  let classSeq = Number(row.classSeq) || 1;
  let preSeq = Number(row.preSeq);
  if (!Number.isFinite(preSeq) || preSeq < 0) preSeq = 0;

  if (budgetClass === BUDGET_CLASS.MAIN) {
    classSeq = 1;
    preSeq = 0;
  } else if (budgetClass === BUDGET_CLASS.DEEMED) {
    preSeq = 0;
    if (!Number.isFinite(Number(row.classSeq)) || Number(row.classSeq) < 1) {
      classSeq = nextClassSeq(others, budgetClass) || 1;
    }
  } else if (budgetClass === BUDGET_CLASS.SUPP) {
    if (!Number.isFinite(Number(row.classSeq)) || Number(row.classSeq) < 1) {
      classSeq = nextClassSeq(others, BUDGET_CLASS.SUPP) || 1;
    }
    if (preSeq > MAX_PRE_SEQ) preSeq = MAX_PRE_SEQ;
  }

  const budgetKindCode = encodeBudgetKindCode({ budgetClass, classSeq, preSeq });
  let roundName = row.roundName;
  if (budgetClass === BUDGET_CLASS.SUPP) {
    const auto =
      preSeq > 0 ? `추경${classSeq}차 성립전${preSeq}` : `추경${classSeq}차`;
    if (!roundName || /^추경\d+차( 성립전\d+)?$/.test(String(roundName)) || roundName === '본예산') {
      roundName = auto;
    }
  }

  return normalizeRoundRow({
    ...row,
    budgetClass,
    classSeq,
    preSeq,
    budgetKindCode,
    roundName,
  });
}

/** YYYYMMDD 또는 YYYY-MM-DD → YYYYMMDD (비교용). 실패 시 '' */
export function toYyyymmdd(value) {
  if (value == null || value === '') return '';
  const digits = String(value).replace(/\D/g, '');
  if (digits.length < 8) return '';
  return digits.slice(0, 8);
}

export function formatYyyymmddDisplay(value) {
  const d = toYyyymmdd(value);
  if (!d) return '';
  return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`;
}

function todayYyyymmdd(now = new Date()) {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${y}${m}${day}`;
}

/** 편성요구 단계: 명칭에 「요구」포함 → 없으면 adjNo 최소 */
export function findRequestAdjStage(adjStages = []) {
  const list = (adjStages || []).filter((s) => s && s.mod !== 'D');
  if (!list.length) return null;
  const byName = list.find((s) => String(s.adjName || '').includes('요구'));
  if (byName) return byName;
  return [...list].sort((a, b) => (Number(a.adjNo) || 0) - (Number(b.adjNo) || 0))[0] || null;
}

/** 요구 단계 여부 */
export function isRequestAdjStage(stage) {
  if (!stage) return false;
  return String(stage.adjName || '').includes('요구');
}

/** 활성 조정차수 정렬 (adjNo 오름차순) */
export function listActiveAdjStages(adjStages = []) {
  return (adjStages || [])
    .filter((s) => s && s.mod !== 'D')
    .slice()
    .sort((a, b) => (Number(a.adjNo) || 0) - (Number(b.adjNo) || 0));
}

/** 현재 조정차수의 직전 단계 (adjNo 바로 이전) */
export function findPreviousAdjStage(adjStages = [], currentAdjNo) {
  const list = listActiveAdjStages(adjStages);
  const cur = Number(currentAdjNo);
  if (!Number.isFinite(cur)) return null;
  const idx = list.findIndex((s) => Number(s.adjNo) === cur);
  if (idx <= 0) return null;
  return list[idx - 1];
}

/**
 * 편성조정 화면용 조정차수 옵션 (요구 단계 제외)
 * @returns {{ value: string, label: string, adjNo: number }[]}
 */
export function listAdjustStageOptions(tenantCode, fiscalYear, budgetKindCode) {
  const round = getBudgetRoundByKindCode(tenantCode, fiscalYear, budgetKindCode);
  if (!round) return [];
  const req = findRequestAdjStage(round.adjStages);
  return listActiveAdjStages(round.adjStages)
    .filter((s) => {
      if (isRequestAdjStage(s)) return false;
      if (req && Number(s.adjNo) === Number(req.adjNo)) return false;
      return true;
    })
    .map((s) => ({
      value: String(s.adjNo),
      label: `${s.adjNo} ${s.adjName || ''}`.trim(),
      adjNo: Number(s.adjNo),
      adjName: s.adjName || '',
    }));
}

function emptyDeptRow(partial = {}) {
  return {
    inoutDiv: '지출',
    deptCode: '',
    deptName: '',
    amtBefore: 0,
    amtAfter: 0,
    submitYn: 'N',
    submitAt: null,
    submitBy: null,
    receiveYn: 'N',
    receiveAt: null,
    receiveBy: null,
    adjustYn: 'N',
    adjustAt: null,
    adjustBy: null,
    ...partial,
  };
}

/** 편성요구 행 → 부서별 예산액(천원) Map */
export function aggregateExpenseAmtByDept(expenseRows = []) {
  const byDept = new Map();
  for (const r of expenseRows || []) {
    if (r.mod === 'D') continue;
    const deptCode = String(r.deptCode || '').trim();
    if (!deptCode) continue;
    const cur = byDept.get(deptCode) || { deptCode, deptName: '', amt: 0 };
    cur.deptName = String(r.deptName || '').trim() || cur.deptName;
    cur.amt += Number(r.budgetAmt) || 0;
    byDept.set(deptCode, cur);
  }
  return byDept;
}

/**
 * 부서별 편성 요구/조정 상태 그리드 행 구성
 * - 사용중 부서 전체 × 지출 행을 기본으로 생성 (부서생성 불필요)
 * - incomeByDept 전달 시 사용중 부서 전체 × 수입 행도 동일하게 생성
 * - 기존 수입 행·제출/접수/조정 플래그 유지
 * - expenseByDept / incomeByDept 집계가 없으면 해당 수지의 기존 금액 유지
 */
export async function buildDeptStatusRows({
  tenantCode,
  stage,
  expenseByDept = new Map(),
  incomeByDept = null,
} = {}) {
  const depts = filterDepts(await loadDepts(tenantCode), { useYn: 'Y' }).slice().sort((a, b) => {
    const so = (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0);
    if (so !== 0) return so;
    return String(a.deptCode || '').localeCompare(String(b.deptCode || ''));
  });

  const existing = (stage?.deptRows || []).filter((d) => d && d.mod !== 'D');
  const existingMap = new Map(
    existing.map((d) => [`${d.inoutDiv || '지출'}|${String(d.deptCode || '').trim()}`, { ...d }])
  );

  const next = [];
  const used = new Set();

  // 1) 전체 부서 × 지출
  for (const dept of depts) {
    const deptCode = String(dept.deptCode || '').trim();
    if (!deptCode) continue;
    const key = `지출|${deptCode}`;
    used.add(key);
    const prev = existingMap.get(key);
    const agg = expenseByDept.get(deptCode);
    const amt = agg != null ? Math.round(agg.amt) || 0 : Number(prev?.amtAfter) || Number(prev?.amtBefore) || 0;
    next.push({
      ...(prev || emptyDeptRow()),
      inoutDiv: '지출',
      deptCode,
      deptName: dept.deptName || prev?.deptName || agg?.deptName || '',
      amtBefore: amt,
      amtAfter: amt,
      submitYn: prev?.submitYn || 'N',
      submitAt: prev?.submitAt ?? null,
      submitBy: prev?.submitBy ?? null,
      receiveYn: prev?.receiveYn || 'N',
      receiveAt: prev?.receiveAt ?? null,
      receiveBy: prev?.receiveBy ?? null,
      adjustYn: prev?.adjustYn || 'N',
      adjustAt: prev?.adjustAt ?? null,
      adjustBy: prev?.adjustBy ?? null,
    });
  }

  // 1b) 전체 부서 × 수입 (수입 동기화 시 incomeByDept 명시 전달)
  if (incomeByDept != null) {
    for (const dept of depts) {
      const deptCode = String(dept.deptCode || '').trim();
      if (!deptCode) continue;
      const key = `수입|${deptCode}`;
      used.add(key);
      const prev = existingMap.get(key);
      const agg = incomeByDept.get(deptCode);
      const amt =
        agg != null
          ? Math.round(agg.amt) || 0
          : Number(prev?.amtAfter) || Number(prev?.amtBefore) || 0;
      next.push({
        ...(prev || emptyDeptRow()),
        inoutDiv: '수입',
        deptCode,
        deptName: dept.deptName || prev?.deptName || agg?.deptName || '',
        amtBefore: amt,
        amtAfter: amt,
        submitYn: prev?.submitYn || 'N',
        submitAt: prev?.submitAt ?? null,
        submitBy: prev?.submitBy ?? null,
        receiveYn: prev?.receiveYn || 'N',
        receiveAt: prev?.receiveAt ?? null,
        receiveBy: prev?.receiveBy ?? null,
        adjustYn: prev?.adjustYn || 'N',
        adjustAt: prev?.adjustAt ?? null,
        adjustBy: prev?.adjustBy ?? null,
      });
    }
  }

  // 2) 기존 수입 행·기타 수지 유지 (부서코드 중복 허용)
  for (const d of existing) {
    const inout = d.inoutDiv || '지출';
    const deptCode = String(d.deptCode || '').trim();
    const key = `${inout}|${deptCode}`;
    if (used.has(key)) continue;
    if (inout === '지출') {
      // 마스터에 없는 지출 부서는 금액/플래그 유지하며 표시
      next.push({ ...d, inoutDiv: '지출', deptCode });
      used.add(key);
      continue;
    }
    next.push({ ...d });
    used.add(key);
  }

  next.sort((a, b) => {
    const io = String(a.inoutDiv || '').localeCompare(String(b.inoutDiv || ''));
    if (io !== 0) return io;
    return String(a.deptCode || '').localeCompare(String(b.deptCode || ''));
  });
  return next;
}

/**
 * 지출예산편성요구 부서별 합계 → 예산차수「요구」단계 deptRows 반영
 * (사용중 부서 리스트 + 편성요구 집계)
 */
export async function syncExpenseRequestToDeptStatus(
  tenantCode,
  fiscalYear,
  budgetKindCode,
  expenseRows = [],
  { inoutDiv = '지출' } = {}
) {
  const code = String(budgetKindCode || '').trim();
  if (!code) return { ok: false, reason: '결합코드가 없습니다.' };

  const rounds = (await loadBudgetRounds(tenantCode, fiscalYear)).map((r) =>
    normalizeRoundRow(r, fiscalYear)
  );
  const idx = rounds.findIndex((r) => r.mod !== 'D' && String(r.budgetKindCode) === code);
  if (idx < 0) {
    return { ok: false, reason: `결합코드 ${code} 차수가 없습니다.` };
  }
  const round = rounds[idx];
  if (round.budgetConfirmYn === 'Y') {
    return { ok: false, reason: '예산이 확정되어 부서별 조정상태에 반영할 수 없습니다.' };
  }

  const reqStage = findRequestAdjStage(round.adjStages);
  if (!reqStage) {
    return {
      ok: false,
      reason: '요구/조정차수가 없습니다. 예산차수등록에서 「부서요구」 등을 추가하세요.',
    };
  }
  if (reqStage.adjConfirmYn === 'Y') {
    return {
      ok: false,
      reason: `요구단계(${reqStage.adjName || ''})가 확정·마감되어 반영할 수 없습니다.`,
    };
  }

  const expenseByDept = aggregateExpenseAmtByDept(expenseRows);
  const nextRows = await buildDeptStatusRows({
    tenantCode,
    stage: reqStage,
    expenseByDept: inoutDiv === '지출' ? expenseByDept : new Map(),
  });

  const stages = (round.adjStages || []).map((s) => {
    if (Number(s.adjNo) !== Number(reqStage.adjNo)) return s;
    return syncStageAmounts({ ...s, deptRows: nextRows });
  });

  const nextRounds = rounds.map((r, i) => (i === idx ? { ...r, adjStages: stages } : r));
  await saveBudgetRounds(tenantCode, fiscalYear, nextRounds);
  return {
    ok: true,
    deptCount: nextRows.filter((d) => d.inoutDiv === '지출').length,
    stageName: reqStage.adjName || `차수${reqStage.adjNo}`,
  };
}

/** 요구단계 지출 부서 제출여부 Map: deptCode → submitYn */
export function getExpenseDeptSubmitMap(tenantCode, fiscalYear, budgetKindCode) {
  const round = getBudgetRoundByKindCode(tenantCode, fiscalYear, budgetKindCode);
  if (!round) return new Map();
  const stage = findRequestAdjStage(round.adjStages);
  if (!stage) return new Map();
  const map = new Map();
  for (const d of stage.deptRows || []) {
    if ((d.inoutDiv || '지출') !== '지출') continue;
    const code = String(d.deptCode || '').trim();
    if (!code) continue;
    map.set(code, d.submitYn === 'Y' ? 'Y' : 'N');
  }
  return map;
}

/**
 * 편성요구 화면 「제출」— 해당 부서들의 요구단계 제출Yn=Y
 */
export async function submitExpenseRequestDepts(
  tenantCode,
  fiscalYear,
  budgetKindCode,
  deptCodes = [],
  { userId = '', expenseRows = [] } = {}
) {
  const code = String(budgetKindCode || '').trim();
  const codes = [...new Set((deptCodes || []).map((c) => String(c || '').trim()).filter(Boolean))];
  if (!code || !codes.length) {
    return { ok: false, reason: '제출할 부서가 없습니다.' };
  }

  // 금액 최신 반영 후 제출 플래그
  const sync = await syncExpenseRequestToDeptStatus(tenantCode, fiscalYear, code, expenseRows, {
    inoutDiv: '지출',
  });
  if (!sync.ok) return sync;

  const rounds = (await loadBudgetRounds(tenantCode, fiscalYear)).map((r) =>
    normalizeRoundRow(r, fiscalYear)
  );
  const idx = rounds.findIndex((r) => r.mod !== 'D' && String(r.budgetKindCode) === code);
  const round = rounds[idx];
  const reqStage = findRequestAdjStage(round.adjStages);
  const targets = codes.map((deptCode) => ({ deptCode, inoutDiv: '지출' }));
  const updated = setDeptFlag(reqStage, targets, 'submit', userId);
  const stages = (round.adjStages || []).map((s) =>
    Number(s.adjNo) === Number(reqStage.adjNo) ? updated : s
  );
  const nextRounds = rounds.map((r, i) => (i === idx ? { ...r, adjStages: stages } : r));
  await saveBudgetRounds(tenantCode, fiscalYear, nextRounds);
  return { ok: true, deptCount: codes.length, stageName: reqStage.adjName || '' };
}

/**
 * 편성요구 화면 「제출취소」— 요구단계 해당 부서 submitYn=N (접수·조정도 해제)
 */
export async function cancelExpenseRequestDepts(
  tenantCode,
  fiscalYear,
  budgetKindCode,
  deptCodes = [],
  { userId = '' } = {}
) {
  const code = String(budgetKindCode || '').trim();
  const codes = [...new Set((deptCodes || []).map((c) => String(c || '').trim()).filter(Boolean))];
  if (!code || !codes.length) {
    return { ok: false, reason: '제출취소할 부서가 없습니다.' };
  }

  const rounds = (await loadBudgetRounds(tenantCode, fiscalYear)).map((r) =>
    normalizeRoundRow(r, fiscalYear)
  );
  const idx = rounds.findIndex((r) => r.mod !== 'D' && String(r.budgetKindCode) === code);
  if (idx < 0) return { ok: false, reason: `결합코드 ${code} 차수가 없습니다.` };
  const round = rounds[idx];
  if (round.budgetConfirmYn === 'Y') {
    return { ok: false, reason: '예산이 확정되어 제출취소할 수 없습니다.' };
  }
  const reqStage = findRequestAdjStage(round.adjStages);
  if (!reqStage) return { ok: false, reason: '요구단계가 없습니다.' };
  if (reqStage.adjConfirmYn === 'Y') {
    return {
      ok: false,
      reason: `요구단계(${reqStage.adjName || ''})가 확정·마감되어 제출취소할 수 없습니다.`,
    };
  }

  const targets = codes.map((deptCode) => ({ deptCode, inoutDiv: '지출' }));
  const updated = setDeptFlag(reqStage, targets, 'submitCancel', userId);
  const stages = (round.adjStages || []).map((s) =>
    Number(s.adjNo) === Number(reqStage.adjNo) ? updated : s
  );
  const nextRounds = rounds.map((r, i) => (i === idx ? { ...r, adjStages: stages } : r));
  await saveBudgetRounds(tenantCode, fiscalYear, nextRounds);
  return { ok: true, deptCount: codes.length, stageName: reqStage.adjName || '' };
}

/** 편성요구 행 → 부서별 예산액 Map (수입·지출 공통) */
export const aggregateIncomeAmtByDept = aggregateExpenseAmtByDept;

/**
 * 수입예산편성요구 부서별 합계 → 예산차수「요구」단계 deptRows 반영
 */
export async function syncIncomeRequestToDeptStatus(
  tenantCode,
  fiscalYear,
  budgetKindCode,
  incomeRows = []
) {
  const code = String(budgetKindCode || '').trim();
  if (!code) return { ok: false, reason: '결합코드가 없습니다.' };

  const rounds = (await loadBudgetRounds(tenantCode, fiscalYear)).map((r) =>
    normalizeRoundRow(r, fiscalYear)
  );
  const idx = rounds.findIndex((r) => r.mod !== 'D' && String(r.budgetKindCode) === code);
  if (idx < 0) {
    return { ok: false, reason: `결합코드 ${code} 차수가 없습니다.` };
  }
  const round = rounds[idx];
  if (round.budgetConfirmYn === 'Y') {
    return { ok: false, reason: '예산이 확정되어 부서별 조정상태에 반영할 수 없습니다.' };
  }

  const reqStage = findRequestAdjStage(round.adjStages);
  if (!reqStage) {
    return {
      ok: false,
      reason: '요구/조정차수가 없습니다. 예산차수등록에서 「부서요구」 등을 추가하세요.',
    };
  }
  if (reqStage.adjConfirmYn === 'Y') {
    return {
      ok: false,
      reason: `요구단계(${reqStage.adjName || ''})가 확정·마감되어 반영할 수 없습니다.`,
    };
  }

  const incomeByDept = aggregateIncomeAmtByDept(incomeRows);
  const nextRows = await buildDeptStatusRows({
    tenantCode,
    stage: reqStage,
    incomeByDept,
  });

  const stages = (round.adjStages || []).map((s) => {
    if (Number(s.adjNo) !== Number(reqStage.adjNo)) return s;
    return syncStageAmounts({ ...s, deptRows: nextRows });
  });

  const nextRounds = rounds.map((r, i) => (i === idx ? { ...r, adjStages: stages } : r));
  await saveBudgetRounds(tenantCode, fiscalYear, nextRounds);
  return {
    ok: true,
    deptCount: nextRows.filter((d) => d.inoutDiv === '수입').length,
    stageName: reqStage.adjName || `차수${reqStage.adjNo}`,
  };
}

/** 요구단계 수입 부서 제출여부 Map: deptCode → submitYn */
export function getIncomeDeptSubmitMap(tenantCode, fiscalYear, budgetKindCode) {
  const round = getBudgetRoundByKindCode(tenantCode, fiscalYear, budgetKindCode);
  if (!round) return new Map();
  const stage = findRequestAdjStage(round.adjStages);
  if (!stage) return new Map();
  const map = new Map();
  for (const d of stage.deptRows || []) {
    if ((d.inoutDiv || '지출') !== '수입') continue;
    const deptCode = String(d.deptCode || '').trim();
    if (!deptCode) continue;
    map.set(deptCode, d.submitYn === 'Y' ? 'Y' : 'N');
  }
  return map;
}

/** 편성요구 화면 「제출」— 수입 부서 submitYn=Y */
export async function submitIncomeRequestDepts(
  tenantCode,
  fiscalYear,
  budgetKindCode,
  deptCodes = [],
  { userId = '', incomeRows = [] } = {}
) {
  const code = String(budgetKindCode || '').trim();
  const codes = [...new Set((deptCodes || []).map((c) => String(c || '').trim()).filter(Boolean))];
  if (!code || !codes.length) {
    return { ok: false, reason: '제출할 부서가 없습니다.' };
  }

  const sync = await syncIncomeRequestToDeptStatus(tenantCode, fiscalYear, code, incomeRows);
  if (!sync.ok) return sync;

  const rounds = (await loadBudgetRounds(tenantCode, fiscalYear)).map((r) =>
    normalizeRoundRow(r, fiscalYear)
  );
  const idx = rounds.findIndex((r) => r.mod !== 'D' && String(r.budgetKindCode) === code);
  const round = rounds[idx];
  const reqStage = findRequestAdjStage(round.adjStages);
  const targets = codes.map((deptCode) => ({ deptCode, inoutDiv: '수입' }));
  const updated = setDeptFlag(reqStage, targets, 'submit', userId);
  const stages = (round.adjStages || []).map((s) =>
    Number(s.adjNo) === Number(reqStage.adjNo) ? updated : s
  );
  const nextRounds = rounds.map((r, i) => (i === idx ? { ...r, adjStages: stages } : r));
  await saveBudgetRounds(tenantCode, fiscalYear, nextRounds);
  return { ok: true, deptCount: codes.length, stageName: reqStage.adjName || '' };
}

/** 편성요구 화면 「제출취소」— 수입 부서 submitYn=N */
export async function cancelIncomeRequestDepts(
  tenantCode,
  fiscalYear,
  budgetKindCode,
  deptCodes = [],
  { userId = '' } = {}
) {
  const code = String(budgetKindCode || '').trim();
  const codes = [...new Set((deptCodes || []).map((c) => String(c || '').trim()).filter(Boolean))];
  if (!code || !codes.length) {
    return { ok: false, reason: '제출취소할 부서가 없습니다.' };
  }

  const rounds = (await loadBudgetRounds(tenantCode, fiscalYear)).map((r) =>
    normalizeRoundRow(r, fiscalYear)
  );
  const idx = rounds.findIndex((r) => r.mod !== 'D' && String(r.budgetKindCode) === code);
  if (idx < 0) return { ok: false, reason: `결합코드 ${code} 차수가 없습니다.` };
  const round = rounds[idx];
  if (round.budgetConfirmYn === 'Y') {
    return { ok: false, reason: '예산이 확정되어 제출취소할 수 없습니다.' };
  }
  const reqStage = findRequestAdjStage(round.adjStages);
  if (!reqStage) return { ok: false, reason: '요구단계가 없습니다.' };
  if (reqStage.adjConfirmYn === 'Y') {
    return {
      ok: false,
      reason: `요구단계(${reqStage.adjName || ''})가 확정·마감되어 제출취소할 수 없습니다.`,
    };
  }

  const targets = codes.map((deptCode) => ({ deptCode, inoutDiv: '수입' }));
  const updated = setDeptFlag(reqStage, targets, 'submitCancel', userId);
  const stages = (round.adjStages || []).map((s) =>
    Number(s.adjNo) === Number(reqStage.adjNo) ? updated : s
  );
  const nextRounds = rounds.map((r, i) => (i === idx ? { ...r, adjStages: stages } : r));
  await saveBudgetRounds(tenantCode, fiscalYear, nextRounds);
  return { ok: true, deptCount: codes.length, stageName: reqStage.adjName || '' };
}

/**
 * 편성조정 금액 → 지정 조정차수 deptRows 반영
 */
export async function syncExpenseAdjustToDeptStatus(
  tenantCode,
  fiscalYear,
  budgetKindCode,
  adjNo,
  expenseRows = [],
  { inoutDiv = '지출' } = {}
) {
  const code = String(budgetKindCode || '').trim();
  if (!code) return { ok: false, reason: '결합코드가 없습니다.' };
  if (adjNo == null || adjNo === '') return { ok: false, reason: '조정차수가 없습니다.' };

  const rounds = (await loadBudgetRounds(tenantCode, fiscalYear)).map((r) =>
    normalizeRoundRow(r, fiscalYear)
  );
  const idx = rounds.findIndex((r) => r.mod !== 'D' && String(r.budgetKindCode) === code);
  if (idx < 0) {
    return { ok: false, reason: `결합코드 ${code} 차수가 없습니다.` };
  }
  const round = rounds[idx];
  if (round.budgetConfirmYn === 'Y') {
    return { ok: false, reason: '예산이 확정되어 부서별 조정상태에 반영할 수 없습니다.' };
  }

  const stage =
    (round.adjStages || []).find((s) => Number(s.adjNo) === Number(adjNo)) || null;
  if (!stage || stage.mod === 'D') {
    return { ok: false, reason: '조정차수를 찾을 수 없습니다.' };
  }
  if (isRequestAdjStage(stage)) {
    return { ok: false, reason: '요구 단계는 편성요구 화면에서 반영하세요.' };
  }
  if (stage.adjConfirmYn === 'Y') {
    return {
      ok: false,
      reason: `조정단계(${stage.adjName || ''})가 확정·마감되어 반영할 수 없습니다.`,
    };
  }

  const expenseByDept = aggregateExpenseAmtByDept(expenseRows);
  const nextRows = await buildDeptStatusRows({
    tenantCode,
    stage,
    expenseByDept: inoutDiv === '지출' ? expenseByDept : new Map(),
  });

  const stages = (round.adjStages || []).map((s) => {
    if (Number(s.adjNo) !== Number(stage.adjNo)) return s;
    return syncStageAmounts({ ...s, deptRows: nextRows });
  });

  const nextRounds = rounds.map((r, i) => (i === idx ? { ...r, adjStages: stages } : r));
  await saveBudgetRounds(tenantCode, fiscalYear, nextRounds);
  return {
    ok: true,
    deptCount: nextRows.filter((d) => d.inoutDiv === '지출').length,
    stageName: stage.adjName || `차수${stage.adjNo}`,
  };
}

/** 지정 조정차수 지출 부서 플래그 Map들 */
export function getExpenseAdjustDeptFlagMaps(tenantCode, fiscalYear, budgetKindCode, adjNo) {
  const empty = { submit: new Map(), receive: new Map(), adjust: new Map() };
  const round = getBudgetRoundByKindCode(tenantCode, fiscalYear, budgetKindCode);
  if (!round) return empty;
  const stage = (round.adjStages || []).find((s) => Number(s.adjNo) === Number(adjNo));
  if (!stage) return empty;
  const submit = new Map();
  const receive = new Map();
  const adjust = new Map();
  for (const d of stage.deptRows || []) {
    if ((d.inoutDiv || '지출') !== '지출') continue;
    const code = String(d.deptCode || '').trim();
    if (!code) continue;
    submit.set(code, d.submitYn === 'Y' ? 'Y' : 'N');
    receive.set(code, d.receiveYn === 'Y' ? 'Y' : 'N');
    adjust.set(code, d.adjustYn === 'Y' ? 'Y' : 'N');
  }
  return { submit, receive, adjust };
}

/** @deprecated 호환 — receive 맵 대신 getExpenseAdjustDeptFlagMaps 사용 권장 */
export function getExpenseAdjustDeptSubmitMap(tenantCode, fiscalYear, budgetKindCode, adjNo) {
  return getExpenseAdjustDeptFlagMaps(tenantCode, fiscalYear, budgetKindCode, adjNo).receive;
}

/**
 * 편성조정 화면 접수/조정 토글
 * @param {'receive'|'receiveCancel'|'adjust'|'adjustCancel'} action
 */
export async function applyExpenseAdjustDeptAction(
  tenantCode,
  fiscalYear,
  budgetKindCode,
  adjNo,
  deptCodes = [],
  action,
  { userId = '', expenseRows = [] } = {}
) {
  const code = String(budgetKindCode || '').trim();
  const codes = [...new Set((deptCodes || []).map((c) => String(c || '').trim()).filter(Boolean))];
  const flagMap = {
    receive: 'receiveAndSubmit',
    receiveCancel: 'receiveCancelAndSubmit',
    adjust: 'adjust',
    adjustCancel: 'adjustCancel',
  };
  const flag = flagMap[action];
  if (!flag) return { ok: false, reason: '알 수 없는 처리입니다.' };
  if (!code || !codes.length) {
    return { ok: false, reason: '대상 부서가 없습니다.' };
  }
  if (adjNo == null || adjNo === '') {
    return { ok: false, reason: '조정차수가 없습니다.' };
  }

  // 접수/조정 확정 전에는 금액 동기화
  if (action === 'receive' || action === 'adjust') {
    const sync = await syncExpenseAdjustToDeptStatus(
      tenantCode,
      fiscalYear,
      code,
      adjNo,
      expenseRows,
      { inoutDiv: '지출' }
    );
    if (!sync.ok) return sync;
  }

  const rounds = (await loadBudgetRounds(tenantCode, fiscalYear)).map((r) =>
    normalizeRoundRow(r, fiscalYear)
  );
  const idx = rounds.findIndex((r) => r.mod !== 'D' && String(r.budgetKindCode) === code);
  if (idx < 0) return { ok: false, reason: `결합코드 ${code} 차수가 없습니다.` };
  const round = rounds[idx];
  if (round.budgetConfirmYn === 'Y') {
    return { ok: false, reason: '예산이 확정되어 처리할 수 없습니다.' };
  }
  const stage = (round.adjStages || []).find((s) => Number(s.adjNo) === Number(adjNo));
  if (!stage) return { ok: false, reason: '조정차수를 찾을 수 없습니다.' };
  if (stage.adjConfirmYn === 'Y') {
    return {
      ok: false,
      reason: `조정단계(${stage.adjName || ''})가 확정·마감되어 처리할 수 없습니다.`,
    };
  }

  const targets = codes.map((deptCode) => ({ deptCode, inoutDiv: '지출' }));
  const updated = setDeptFlag(stage, targets, flag, userId);
  const stages = (round.adjStages || []).map((s) =>
    Number(s.adjNo) === Number(stage.adjNo) ? updated : s
  );
  const nextRounds = rounds.map((r, i) => (i === idx ? { ...r, adjStages: stages } : r));
  await saveBudgetRounds(tenantCode, fiscalYear, nextRounds);
  return { ok: true, deptCount: codes.length, stageName: stage.adjName || '', action };
}

/**
 * 수입예산편성조정 금액 → 지정 조정차수 deptRows 반영
 */
export async function syncIncomeAdjustToDeptStatus(
  tenantCode,
  fiscalYear,
  budgetKindCode,
  adjNo,
  incomeRows = []
) {
  const code = String(budgetKindCode || '').trim();
  if (!code) return { ok: false, reason: '결합코드가 없습니다.' };
  if (adjNo == null || adjNo === '') return { ok: false, reason: '조정차수가 없습니다.' };

  const rounds = (await loadBudgetRounds(tenantCode, fiscalYear)).map((r) =>
    normalizeRoundRow(r, fiscalYear)
  );
  const idx = rounds.findIndex((r) => r.mod !== 'D' && String(r.budgetKindCode) === code);
  if (idx < 0) {
    return { ok: false, reason: `결합코드 ${code} 차수가 없습니다.` };
  }
  const round = rounds[idx];
  if (round.budgetConfirmYn === 'Y') {
    return { ok: false, reason: '예산이 확정되어 부서별 조정상태에 반영할 수 없습니다.' };
  }

  const stage =
    (round.adjStages || []).find((s) => Number(s.adjNo) === Number(adjNo)) || null;
  if (!stage || stage.mod === 'D') {
    return { ok: false, reason: '조정차수를 찾을 수 없습니다.' };
  }
  if (isRequestAdjStage(stage)) {
    return { ok: false, reason: '요구 단계는 편성요구 화면에서 반영하세요.' };
  }
  if (stage.adjConfirmYn === 'Y') {
    return {
      ok: false,
      reason: `조정단계(${stage.adjName || ''})가 확정·마감되어 반영할 수 없습니다.`,
    };
  }

  const incomeByDept = aggregateIncomeAmtByDept(incomeRows);
  const nextRows = await buildDeptStatusRows({
    tenantCode,
    stage,
    incomeByDept,
  });

  const stages = (round.adjStages || []).map((s) => {
    if (Number(s.adjNo) !== Number(stage.adjNo)) return s;
    return syncStageAmounts({ ...s, deptRows: nextRows });
  });

  const nextRounds = rounds.map((r, i) => (i === idx ? { ...r, adjStages: stages } : r));
  await saveBudgetRounds(tenantCode, fiscalYear, nextRounds);
  return {
    ok: true,
    deptCount: nextRows.filter((d) => d.inoutDiv === '수입').length,
    stageName: stage.adjName || `차수${stage.adjNo}`,
  };
}

/** 지정 조정차수 수입 부서 플래그 Map들 */
export function getIncomeAdjustDeptFlagMaps(tenantCode, fiscalYear, budgetKindCode, adjNo) {
  const empty = { submit: new Map(), receive: new Map(), adjust: new Map() };
  const round = getBudgetRoundByKindCode(tenantCode, fiscalYear, budgetKindCode);
  if (!round) return empty;
  const stage = (round.adjStages || []).find((s) => Number(s.adjNo) === Number(adjNo));
  if (!stage) return empty;
  const submit = new Map();
  const receive = new Map();
  const adjust = new Map();
  for (const d of stage.deptRows || []) {
    if ((d.inoutDiv || '지출') !== '수입') continue;
    const deptCode = String(d.deptCode || '').trim();
    if (!deptCode) continue;
    submit.set(deptCode, d.submitYn === 'Y' ? 'Y' : 'N');
    receive.set(deptCode, d.receiveYn === 'Y' ? 'Y' : 'N');
    adjust.set(deptCode, d.adjustYn === 'Y' ? 'Y' : 'N');
  }
  return { submit, receive, adjust };
}

/**
 * 편성조정 화면 접수/조정 토글 (수입)
 */
export async function applyIncomeAdjustDeptAction(
  tenantCode,
  fiscalYear,
  budgetKindCode,
  adjNo,
  deptCodes = [],
  action,
  { userId = '', incomeRows = [] } = {}
) {
  const code = String(budgetKindCode || '').trim();
  const codes = [...new Set((deptCodes || []).map((c) => String(c || '').trim()).filter(Boolean))];
  const flagMap = {
    receive: 'receiveAndSubmit',
    receiveCancel: 'receiveCancelAndSubmit',
    adjust: 'adjust',
    adjustCancel: 'adjustCancel',
  };
  const flag = flagMap[action];
  if (!flag) return { ok: false, reason: '알 수 없는 처리입니다.' };
  if (!code || !codes.length) {
    return { ok: false, reason: '대상 부서가 없습니다.' };
  }
  if (adjNo == null || adjNo === '') {
    return { ok: false, reason: '조정차수가 없습니다.' };
  }

  if (action === 'receive' || action === 'adjust') {
    const sync = await syncIncomeAdjustToDeptStatus(
      tenantCode,
      fiscalYear,
      code,
      adjNo,
      incomeRows
    );
    if (!sync.ok) return sync;
  }

  const rounds = (await loadBudgetRounds(tenantCode, fiscalYear)).map((r) =>
    normalizeRoundRow(r, fiscalYear)
  );
  const idx = rounds.findIndex((r) => r.mod !== 'D' && String(r.budgetKindCode) === code);
  if (idx < 0) return { ok: false, reason: `결합코드 ${code} 차수가 없습니다.` };
  const round = rounds[idx];
  if (round.budgetConfirmYn === 'Y') {
    return { ok: false, reason: '예산이 확정되어 처리할 수 없습니다.' };
  }
  const stage = (round.adjStages || []).find((s) => Number(s.adjNo) === Number(adjNo));
  if (!stage) return { ok: false, reason: '조정차수를 찾을 수 없습니다.' };
  if (stage.adjConfirmYn === 'Y') {
    return {
      ok: false,
      reason: `조정단계(${stage.adjName || ''})가 확정·마감되어 처리할 수 없습니다.`,
    };
  }

  const targets = codes.map((deptCode) => ({ deptCode, inoutDiv: '수입' }));
  const updated = setDeptFlag(stage, targets, flag, userId);
  const stages = (round.adjStages || []).map((s) =>
    Number(s.adjNo) === Number(stage.adjNo) ? updated : s
  );
  const nextRounds = rounds.map((r, i) => (i === idx ? { ...r, adjStages: stages } : r));
  await saveBudgetRounds(tenantCode, fiscalYear, nextRounds);
  return { ok: true, deptCount: codes.length, stageName: stage.adjName || '', action };
}

/** @deprecated 편성조정은 접수/조정 사용 — 호환용 래퍼 */
export function submitExpenseAdjustDepts(
  tenantCode,
  fiscalYear,
  budgetKindCode,
  adjNo,
  deptCodes = [],
  opts = {}
) {
  return applyExpenseAdjustDeptAction(
    tenantCode,
    fiscalYear,
    budgetKindCode,
    adjNo,
    deptCodes,
    'receive',
    opts
  );
}

/**
 * 편성조정 입력 가능 컨텍스트 (선택 조정차수 기준)
 */
export function resolveExpenseAdjustContext(
  tenantCode,
  fiscalYear,
  budgetKindCode,
  adjNo,
  now = new Date()
) {
  const today = todayYyyymmdd(now);
  const code = String(budgetKindCode || '').trim();
  const empty = {
    ok: false,
    canEdit: false,
    periodOk: false,
    round: null,
    reqStage: null,
    adjStage: null,
    prevStage: null,
    budgetKindCode: code,
    adjNo: adjNo != null ? Number(adjNo) : null,
    applyDate: '',
    applyDateDisp: '',
    inputDeadline: '',
    inputDeadlineDisp: '',
    today,
  };

  const rounds = getCachedBudgetRounds(tenantCode, fiscalYear).filter((r) => r.mod !== 'D');
  if (!rounds.length) {
    return {
      ...empty,
      reason: '예산차수가 없습니다. 예산차수등록에서 먼저 등록하세요.',
      editBlockReason: '예산차수가 없어 편성조정을 입력할 수 없습니다.',
    };
  }
  if (!code) {
    return {
      ...empty,
      ok: true,
      reason: '예산차수(결합코드)를 선택하세요.',
      editBlockReason: '예산차수를 선택한 뒤 조회·입력하세요.',
    };
  }

  const round = rounds.find((r) => String(r.budgetKindCode) === code) || null;
  if (!round) {
    return {
      ...empty,
      reason: `결합코드 ${code} 차수가 없습니다.`,
      editBlockReason: '선택한 예산차수가 없어 입력할 수 없습니다.',
    };
  }

  const reqStage = findRequestAdjStage(round.adjStages);
  const adjStage =
    adjNo != null && adjNo !== ''
      ? (round.adjStages || []).find((s) => Number(s.adjNo) === Number(adjNo) && s.mod !== 'D')
      : null;
  const prevStage = adjStage ? findPreviousAdjStage(round.adjStages, adjStage.adjNo) : null;

  const applyDate = toYyyymmdd(round.applyDate);
  const inputDeadline = toYyyymmdd(adjStage?.inputDeadline);
  const applyDateDisp = formatYyyymmddDisplay(applyDate);
  const inputDeadlineDisp = formatYyyymmddDisplay(inputDeadline);

  let periodOk = true;
  let periodWarn = '';
  if (applyDate && today < applyDate) {
    periodOk = false;
    periodWarn = `적용일(${applyDateDisp}) 이전입니다. (참고)`;
  } else if (inputDeadline && today > inputDeadline) {
    periodOk = false;
    periodWarn = `입력마감일(${inputDeadlineDisp})이 지났습니다. (참고 · 마감 처리와 무관)`;
  } else if (!applyDate || !inputDeadline) {
    periodOk = false;
    periodWarn = '적용일 또는 조정단계 입력마감일이 없습니다. 예산차수등록에서 확인하세요.';
  }

  const base = {
    ok: true,
    round,
    reqStage,
    adjStage,
    prevStage,
    budgetKindCode: code,
    adjNo: adjStage ? Number(adjStage.adjNo) : null,
    applyDate,
    applyDateDisp,
    inputDeadline,
    inputDeadlineDisp,
    today,
    periodOk,
    periodWarn: periodWarn || undefined,
  };

  if (!adjStage) {
    return {
      ...base,
      canEdit: false,
      editBlockReason: '조정차수(1차조정 등)를 선택한 뒤 조회·입력하세요.',
    };
  }
  if (isRequestAdjStage(adjStage)) {
    return {
      ...base,
      canEdit: false,
      editBlockReason: '요구 단계는 「지출예산편성요구」 화면에서 입력하세요.',
    };
  }
  if (round.budgetConfirmYn === 'Y') {
    return {
      ...base,
      canEdit: false,
      editBlockReason: `결합코드 ${code} 예산이 확정되어 편성조정을 수정할 수 없습니다.`,
    };
  }
  if (adjStage.adjConfirmYn === 'Y') {
    return {
      ...base,
      canEdit: false,
      editBlockReason: `조정단계(${adjStage.adjName || ''})가 확정·마감되어 수정할 수 없습니다.`,
    };
  }

  return { ...base, canEdit: true };
}

/** 편성조정 입력마감 옆 상태 (확정=선택 조정차수 · 접수=부서 접수) */
export function resolveExpenseAdjustStatusLabel(ctx, options = {}) {
  if (!ctx?.budgetKindCode) return '';
  if (ctx.round?.budgetConfirmYn === 'Y') return '확정';
  if (ctx.adjStage?.adjConfirmYn === 'Y') return '확정';

  const raw = options.deptCodes;
  const codes = Array.isArray(raw)
    ? raw.map((c) => String(c || '').trim()).filter(Boolean)
    : String(raw || '')
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean);
  const map = options.deptSubmitMap;
  if (map && codes.length > 0 && codes.every((c) => map.get(c) === 'Y')) {
    return '접수';
  }

  if (ctx.applyDate && ctx.today < ctx.applyDate) return '적용일 미도래';
  if (ctx.inputDeadline && ctx.today > ctx.inputDeadline) return '마감일 초과';
  if (!ctx.ok) return '';
  return '입력가능';
}

/**
 * 팝업 오픈 시: 부서 리스트 + 편성요구 집계로 deptRows 재구성·저장
 */
export async function refreshDeptStatusForStage(
  tenantCode,
  fiscalYear,
  budgetKindCode,
  adjNo,
  expenseRows = []
) {
  const rounds = (await loadBudgetRounds(tenantCode, fiscalYear)).map((r) =>
    normalizeRoundRow(r, fiscalYear)
  );
  const idx = rounds.findIndex(
    (r) => r.mod !== 'D' && String(r.budgetKindCode) === String(budgetKindCode || '')
  );
  if (idx < 0) return { ok: false, reason: '차수를 찾을 수 없습니다.', rows: [] };
  const round = rounds[idx];
  const stage =
    (round.adjStages || []).find((s) => Number(s.adjNo) === Number(adjNo)) ||
    findRequestAdjStage(round.adjStages);
  if (!stage) return { ok: false, reason: '조정차수가 없습니다.', rows: [] };

  const expenseByDept = aggregateExpenseAmtByDept(expenseRows);
  const nextRows = await buildDeptStatusRows({
    tenantCode,
    stage,
    expenseByDept,
  });
  const stages = (round.adjStages || []).map((s) =>
    Number(s.adjNo) === Number(stage.adjNo) ? syncStageAmounts({ ...s, deptRows: nextRows }) : s
  );
  const nextRounds = rounds.map((r, i) => (i === idx ? { ...r, adjStages: stages } : r));
  await saveBudgetRounds(tenantCode, fiscalYear, nextRounds);
  return { ok: true, rows: nextRows, stageName: stage.adjName || '' };
}

/**
 * 편성요구 select 옵션 (결합코드 오름차순)
 * @returns {{ value: string, label: string, roundName: string }[]}
 */
export function listBudgetKindOptions(tenantCode, fiscalYear) {
  return getCachedBudgetRounds(tenantCode, fiscalYear)
    .filter((r) => r.mod !== 'D' && r.budgetKindCode)
    .slice()
    .sort((a, b) => String(a.budgetKindCode).localeCompare(String(b.budgetKindCode)))
    .map((r) => ({
      value: String(r.budgetKindCode),
      label: `${r.budgetKindCode} ${r.roundName || ''}`.trim(),
      roundName: r.roundName || '',
    }));
}

/**
 * 네이티브 select는 value가 옵션에 없으면 첫 항목만 표시하고 폼 값은 비어 둔다.
 * 현재 값이 유효하면 유지, 없으면 본예산(100), 그것도 없으면 첫 옵션.
 */
export function pickDefaultBudgetKindCode(opts, current) {
  const list = Array.isArray(opts) ? opts : [];
  const cur = String(current || '').trim();
  if (cur && list.some((o) => String(o.value) === cur)) return cur;
  if (list.some((o) => String(o.value) === MAIN_KIND_CODE)) return MAIN_KIND_CODE;
  return list[0]?.value ? String(list[0].value) : '';
}

/** API 모드에서 동기 listBudgetKindOptions 전에 차수·조정단계 캐시를 채운다. */
export async function refreshBudgetKindOptions(tenantCode, fiscalYear) {
  const key = roundCacheKey(tenantCode, fiscalYear);
  if (cacheGet(roundCache, key) == null) {
    await loadBudgetRounds(tenantCode, fiscalYear);
  }
  return listBudgetKindOptions(tenantCode, fiscalYear);
}

export function getBudgetRoundByKindCode(tenantCode, fiscalYear, budgetKindCode) {
  const code = String(budgetKindCode || '').trim();
  if (!code) return null;
  return (
    getCachedBudgetRounds(tenantCode, fiscalYear).find(
      (r) => r.mod !== 'D' && String(r.budgetKindCode) === code
    ) || null
  );
}

/**
 * 편성요구 입력 컨텍스트 (적용일·요구 마감일·편집 가능 여부)
 *
 * 하드 잠금(canEdit=false): 차수 없음 / 미선택 / 예산확정 / 요구단계 조정확정(마감 액션)
 * 적용일·입력마감일: 확인용 표시 + periodWarn (달력만으로 버튼 잠그지 않음 — 마감 액션 전제)
 *
 * @returns {{
 *   ok: boolean,
 *   reason?: string,
 *   canEdit: boolean,
 *   editBlockReason?: string,
 *   periodOk: boolean,
 *   periodWarn?: string,
 *   round: object|null,
 *   reqStage: object|null,
 *   budgetKindCode: string,
 *   applyDate: string,
 *   applyDateDisp: string,
 *   inputDeadline: string,
 *   inputDeadlineDisp: string,
 *   today: string,
 * }}
 */
export function resolveExpenseRequestContext(tenantCode, fiscalYear, budgetKindCode, now = new Date()) {
  const today = todayYyyymmdd(now);
  const code = String(budgetKindCode || '').trim();
  const empty = {
    ok: false,
    canEdit: false,
    periodOk: false,
    round: null,
    reqStage: null,
    budgetKindCode: code,
    applyDate: '',
    applyDateDisp: '',
    inputDeadline: '',
    inputDeadlineDisp: '',
    today,
  };

  const rounds = getCachedBudgetRounds(tenantCode, fiscalYear).filter((r) => r.mod !== 'D');
  if (!rounds.length) {
    return {
      ...empty,
      reason: '예산차수가 없습니다. 예산차수등록에서 본예산(100)·추경 등을 먼저 등록하세요.',
      editBlockReason: '예산차수가 없어 편성요구를 입력할 수 없습니다.',
    };
  }

  if (!code) {
    return {
      ...empty,
      ok: true,
      reason: '예산차수(결합코드)를 선택하세요.',
      editBlockReason: '예산차수를 선택한 뒤 조회·입력하세요.',
    };
  }

  const round = rounds.find((r) => String(r.budgetKindCode) === code) || null;
  if (!round) {
    return {
      ...empty,
      reason: `결합코드 ${code} 차수가 없습니다.`,
      editBlockReason: '선택한 예산차수가 없어 입력할 수 없습니다.',
    };
  }

  const reqStage = findRequestAdjStage(round.adjStages);
  const applyDate = toYyyymmdd(round.applyDate);
  const inputDeadline = toYyyymmdd(reqStage?.inputDeadline);
  const applyDateDisp = formatYyyymmddDisplay(applyDate);
  const inputDeadlineDisp = formatYyyymmddDisplay(inputDeadline);

  let periodOk = true;
  let periodWarn = '';
  if (applyDate && today < applyDate) {
    periodOk = false;
    periodWarn = `적용일(${applyDateDisp}) 이전입니다. (참고)`;
  } else if (inputDeadline && today > inputDeadline) {
    periodOk = false;
    periodWarn = `입력마감일(${inputDeadlineDisp})이 지났습니다. (참고 · 마감 처리와 무관)`;
  } else if (!applyDate || !inputDeadline) {
    periodOk = false;
    periodWarn = '적용일 또는 요구단계 입력마감일이 없습니다. 예산차수등록에서 확인하세요.';
  }

  const base = {
    ok: true,
    round,
    reqStage,
    budgetKindCode: code,
    applyDate,
    applyDateDisp,
    inputDeadline,
    inputDeadlineDisp,
    today,
    periodOk,
    periodWarn: periodWarn || undefined,
  };

  // 예산 확정(마감 액션) 후에만 하드 잠금
  if (round.budgetConfirmYn === 'Y') {
    return {
      ...base,
      canEdit: false,
      editBlockReason: `결합코드 ${code} 예산이 확정되어 편성요구를 수정할 수 없습니다.`,
    };
  }
  // 요구단계 조정확정 = 요구 마감 액션 완료
  if (reqStage?.adjConfirmYn === 'Y') {
    return {
      ...base,
      canEdit: false,
      editBlockReason: `요구단계(${reqStage.adjName || '부서요구'})가 확정·마감되어 수정할 수 없습니다.`,
    };
  }

  return { ...base, canEdit: true };
}

/**
 * 편성요구 조회조건 · 입력마감 옆 표시용 상태
 * 우선순위: 확정 > 제출(대상 부서) > 적용일 미도래 > 마감일 초과 > 입력가능
 * - deptCodes: 조회 부서 또는 (미지정 시) 화면 로드 행의 부서 목록
 *
 * @param {ReturnType<typeof resolveExpenseRequestContext>} ctx
 * @param {{ deptCodes?: string|string[], deptSubmitMap?: Map<string,string> }} [options]
 * @returns {string}
 */
export function resolveExpenseInputStatusLabel(ctx, options = {}) {
  if (!ctx?.budgetKindCode) return '';

  if (ctx.round?.budgetConfirmYn === 'Y') return '확정';
  if (ctx.reqStage?.adjConfirmYn === 'Y') return '확정';

  const raw = options.deptCodes;
  const codes = Array.isArray(raw)
    ? raw.map((c) => String(c || '').trim()).filter(Boolean)
    : String(raw || '')
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean);
  const map = options.deptSubmitMap;
  // 대상 부서가 모두 제출Yn=Y 이면 「제출」(조회조건 부서 없어도 로드 행 부서 기준)
  if (map && codes.length > 0 && codes.every((c) => map.get(c) === 'Y')) {
    return '제출';
  }

  if (ctx.applyDate && ctx.today < ctx.applyDate) return '적용일 미도래';
  if (ctx.inputDeadline && ctx.today > ctx.inputDeadline) return '마감일 초과';

  if (!ctx.ok) return '';
  return '입력가능';
}

/** 수입 편성요구·조정 — 동일 차수·마감일 규칙 (잠금 문구만 수입 화면명) */
export const resolveIncomeRequestContext = resolveExpenseRequestContext;
export const resolveIncomeInputStatusLabel = resolveExpenseInputStatusLabel;
export const resolveIncomeAdjustStatusLabel = resolveExpenseAdjustStatusLabel;

export function resolveIncomeAdjustContext(
  tenantCode,
  fiscalYear,
  budgetKindCode,
  adjNo,
  now = new Date()
) {
  const ctx = resolveExpenseAdjustContext(
    tenantCode,
    fiscalYear,
    budgetKindCode,
    adjNo,
    now
  );
  if (
    ctx?.editBlockReason &&
    String(ctx.editBlockReason).includes('지출예산편성요구')
  ) {
    return {
      ...ctx,
      editBlockReason: '요구 단계는 「수입예산편성요구」 화면에서 입력하세요.',
    };
  }
  return ctx;
}
